import { useLocalePathname } from "@i18n/routing";
import { trackAiEditImageEvent } from "@saas/ai-edit-image/lib/analytics";
import { useLoginModal } from "@saas/auth/components/LoginModalProvider";
import { useSession } from "@saas/auth/hooks/use-session";
import { useCredits } from "@saas/credits/hooks/use-credits";
import { usePurchases } from "@saas/payments/hooks/purchases";
import { usePostGenerationPaywall } from "@saas/payments/hooks/use-post-generation-paywall";
import { GENERATION_HISTORY_QUERY_KEY } from "@saas/shared/history/types";
import { useRouter } from "@shared/hooks/router";
import { apiClient } from "@shared/lib/api-client";
import { isImageGenerationTimeout } from "@shared/lib/image-generation-error";
import {
	PENDING_GENERATOR_STATE_KEY,
	saveBlobPendingState,
} from "@shared/lib/pending-state";
import { reportGenerationFailure } from "@shared/lib/report-generation-failure";
import {
	addCanvasWatermark,
	shouldAddWatermarkOnClient,
} from "@shared/lib/watermark-canvas";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { getImageModelSettings } from "../config/model-settings";
import { countImageRefs } from "../lib/image-refs";
import {
	type ImageTaskSubmitted,
	isImageTaskSubmitted,
	waitForImageTask,
} from "../lib/image-task";
import {
	DEFAULT_MODEL_CONFIGS,
	getCreditCostAfterSwitch,
	getImageGenerationCreditCost,
	paidModelPaywallReason,
	supportsAutoAspectRatio,
	supportsImageResolution,
} from "../types";
import type { ImageResolution } from "../types";
import type {
	AIModel,
	ImageGenerationResponse,
	ImageGeneratorApiModel,
	ImageStyle,
	ModelConfig,
	PromptHistoryState,
	PromptVersion,
	Template,
} from "../types";

// Define an interface for the error response
interface ErrorResponse {
	error?: string;
	message?: string;
	details?: string;
}

type MutationError = Error & {
	code?: string;
	status?: number;
};

/** 一张"生成中"的占位卡片，形状对齐 ImageHistoryCard 读的那几个字段 */
export type PendingHistoryItem = {
	id: string;
	originalPrompt: string;
	model: string;
	imageUrls: string[];
	inputImageUrls?: string[];
	inputType: "text" | "image";
	createdAt: string;
	status: "PENDING";
	parameters?: { num_outputs?: number };
	metadata?: {
		timestamp?: string;
		parameters?: {
			model?: string;
			aspect_ratio?: string;
			num_outputs?: number;
			resolution?: string;
		};
	};
};

type PromptTabKey = "text-to-image" | "image-to-image" | "templates";

function resolvePromptTabKey(
	tab?: "text-to-image" | "image-to-image" | "templates",
): PromptTabKey {
	if (tab === "text-to-image") return "text-to-image";
	if (tab === "templates") return "templates";
	return "image-to-image";
}

type TemplateUploadState = {
	urls: string[];
	timestamp: number;
};

const TEMPLATE_UPLOADS_STORAGE_PREFIX = "aiImageGenerator:templateUploads:";

function templateUploadsStorageKey(templateId: string) {
	return `${TEMPLATE_UPLOADS_STORAGE_PREFIX}${templateId}`;
}

function readTemplateUploads(templateId: string): string[] | null {
	try {
		const raw = localStorage.getItem(templateUploadsStorageKey(templateId));
		if (!raw) return null;
		const parsed = JSON.parse(raw) as Partial<TemplateUploadState>;
		if (!Array.isArray(parsed.urls)) return null;
		return parsed.urls
			.filter((url): url is string => typeof url === "string")
			.filter((url) => !url.startsWith("data:"));
	} catch {
		return null;
	}
}

function writeTemplateUploads(templateId: string, urls: string[]) {
	try {
		const state: TemplateUploadState = { urls, timestamp: Date.now() };
		localStorage.setItem(
			templateUploadsStorageKey(templateId),
			JSON.stringify(state),
		);
	} catch {
		// ignore storage quota / privacy mode errors
	}
}

function clearTemplateUploads(templateId: string) {
	try {
		localStorage.removeItem(templateUploadsStorageKey(templateId));
	} catch {
		// ignore
	}
}

export function useAiImageGenerator(
	activeTab?: "text-to-image" | "image-to-image" | "templates",
	defaultModel?: AIModel,
) {
	const [selectedModel, setSelectedModel] = useState<AIModel>(
		defaultModel ?? "v3.0",
	);
	const activePromptTabKey = resolvePromptTabKey(activeTab);
	const [promptByTab, setPromptByTab] = useState<
		Record<PromptTabKey, string>
	>({
		"text-to-image": "",
		"image-to-image": "",
		templates: "",
	});
	const prompt = promptByTab[activePromptTabKey];
	const setPrompt = useCallback(
		(
			newPrompt: string,
			tab?: "text-to-image" | "image-to-image" | "templates",
		) => {
			const tabKey = resolvePromptTabKey(tab ?? activeTab);
			setPromptByTab((prev) => ({
				...prev,
				[tabKey]: newPrompt,
			}));
		},
		[activeTab],
	);
	const [style, setStyle] = useState<ImageStyle>("none");
	const [generatedImage, setGeneratedImage] = useState<string | null>(null);
	const [originalImageUrl, setOriginalImageUrl] = useState<string | null>(
		null,
	);
	const [inputImages, setInputImages] = useState<string[]>([]); // Base64 images for image-to-image mode
	const {
		getRemainingCredits,
		getRemainingCreditsWhenLoaded,
		useCredit,
		isUsingCredit,
		isLoading: isCreditsLoading,
	} = useCredits();
	const [aspectRatio, setAspectRatio] = useState("1:1");
	const [imageCount, setImageCount] = useState(1);
	const [resolution, setResolution] = useState<ImageResolution>("1K");
	// 切到档位更少的模型（qwen3 / seedream 5 只有 1K/2K）时，落回它的最高档。
	// 不拉的话界面还显示 4K，后端按 1K 计价出图（KIE_MODEL_IDS 的 qualityParam 兜底）
	useEffect(() => {
		if (!supportsImageResolution(selectedModel)) {
			return;
		}
		const allowed = getImageModelSettings(selectedModel).resolutions;
		if (!allowed.includes(resolution)) {
			setResolution(allowed[allowed.length - 1]);
		}
	}, [selectedModel, resolution]);
	const [watermark, setWatermark] = useState(true);
	const [showUpgradeModal, setShowUpgradeModal] = useState(false);
	// hook 内部打开升级弹窗时，顺带说明是为什么打开的。组件拿它决定弹窗文案，
	// 没有值时退回默认的「积分不足」。
	const [pendingUpgradeReason, setPendingUpgradeReason] = useState<
		"nano-banana-pro" | "paid-model" | "post-generation" | null
	>(null);
	const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
		null,
	);
	const { user } = useSession();
	const { showLoginModal } = useLoginModal();
	const {
		activePlan,
		canRemoveWatermark,
		loading: purchasesLoading,
	} = usePurchases();
	// 已经开着别的付费墙就不覆盖它的文案
	const schedulePostGenerationPaywall = usePostGenerationPaywall(() => {
		if (showUpgradeModal) {
			return;
		}
		setPendingUpgradeReason("post-generation");
		setShowUpgradeModal(true);
	});
	const router = useRouter();
	const pathname = usePathname();
	// 出图时随请求上报，落 metadata.page 记这张图在哪个页面出的（去掉语言前缀，
	// /templates/* 保留具体模板）；Regenerate 等所有出图都走下面两个 mutation
	const sourcePage = useLocalePathname();
	const queryClient = useQueryClient();
	const t = useTranslations("aiImageGenerator.client");
	// 使用静态配置作为初始值，避免UI闪烁
	const [modelConfigs, setModelConfigs] = useState<
		Record<string, ModelConfig>
	>(DEFAULT_MODEL_CONFIGS);

	const getCurrentPath = useCallback(() => {
		if (typeof window !== "undefined") {
			return window.location.pathname + window.location.search;
		}
		return pathname;
	}, [pathname]);

	const savePendingGeneratorState = useCallback(() => {
		// inputImages 多数时候是 R2 地址，但模板参考图和 photo-to-pixar 那类转换流
		// 塞的是 base64。IndexedDB 没有 localStorage 那 5MB 配额，所以不用再判断
		// 体积、也不用「太大就把图丢掉」。
		// 返回 Promise：写入是异步的，调用方必须 await 完再弹登录，否则用户点
		// OAuth 整页跳走时这次写入可能还没提交，登录回来现场就没了。
		return saveBlobPendingState(PENDING_GENERATOR_STATE_KEY, {
			prompt,
			selectedModel,
			aspectRatio,
			imageCount,
			resolution,
			isPublic: false,
			watermark,
			inputImages,
			activeTab: activeTab || "image-to-image",
			style,
		});
	}, [
		activeTab,
		aspectRatio,
		imageCount,
		inputImages,
		prompt,
		resolution,
		selectedModel,
		style,
		watermark,
	]);

	// 获取模型配置 - 现在主要用于更新可能的变化，而不是初始化
	useQuery({
		queryKey: ["modelConfigs"],
		queryFn: async () => {
			const response = await fetch("/api/image-generator/models");
			if (!response.ok) throw new Error("Failed to fetch model configs");
			const data = await response.json();
			setModelConfigs(data.models);
			return data.models;
		},
	});

	// 提示词版本历史记录状态
	const [promptVersionsByTab, setPromptVersionsByTab] = useState<
		Record<PromptTabKey, PromptHistoryState>
	>({
		"text-to-image": { versions: [], currentIndex: -1 },
		"image-to-image": { versions: [], currentIndex: -1 },
		templates: { versions: [], currentIndex: -1 },
	});
	const promptVersions = promptVersionsByTab[activePromptTabKey];

	// 更新 prompt 的函数，同时记录历史
	const updatePrompt = useCallback(
		(newPrompt: string) => {
			setPrompt(newPrompt);

			// 只有在有实际内容时才添加新版本到历史记录
			if (newPrompt.trim()) {
				setPromptVersionsByTab((prev) => {
					const current = prev[activePromptTabKey];

					// 如果当前不是最新版本，截断历史记录
					const newVersions =
						current.currentIndex < current.versions.length - 1
							? current.versions.slice(
									0,
									current.currentIndex + 1,
								)
							: [...current.versions];

					// 添加新版本
					const newVersion: PromptVersion = {
						prompt: newPrompt,
						timestamp: Date.now(),
					};

					return {
						...prev,
						[activePromptTabKey]: {
							versions: [...newVersions, newVersion],
							currentIndex: newVersions.length,
						},
					};
				});
			}
		},
		[activePromptTabKey, setPrompt],
	);

	// 初始化 prompt 历史
	useEffect(() => {
		if (prompt.trim() && promptVersions.versions.length === 0) {
			setPromptVersionsByTab((prev) => ({
				...prev,
				[activePromptTabKey]: {
					versions: [{ prompt, timestamp: Date.now() }],
					currentIndex: 0,
				},
			}));
		}
	}, [activePromptTabKey, prompt, promptVersions.versions.length]);

	// Set defaults for users who can have watermark-free output
	const isSubscribed = !purchasesLoading && canRemoveWatermark;
	useEffect(() => {
		if (isSubscribed) {
			setWatermark(false); // No watermark by default for subscribed users
		}
	}, [isSubscribed]);

	// 撤销操作
	const handleUndo = useCallback(() => {
		const { versions, currentIndex } =
			promptVersionsByTab[activePromptTabKey];

		if (currentIndex > 0) {
			const newIndex = currentIndex - 1;
			setPrompt(versions[newIndex].prompt);
			setPromptVersionsByTab((prev) => ({
				...prev,
				[activePromptTabKey]: {
					...prev[activePromptTabKey],
					currentIndex: newIndex,
				},
			}));
		}
	}, [activePromptTabKey, promptVersionsByTab, setPrompt]);

	// 重做操作
	const handleRedo = useCallback(() => {
		const { versions, currentIndex } =
			promptVersionsByTab[activePromptTabKey];

		if (currentIndex < versions.length - 1) {
			const newIndex = currentIndex + 1;
			setPrompt(versions[newIndex].prompt);
			setPromptVersionsByTab((prev) => ({
				...prev,
				[activePromptTabKey]: {
					...prev[activePromptTabKey],
					currentIndex: newIndex,
				},
			}));
		}
	}, [activePromptTabKey, promptVersionsByTab, setPrompt]);

	// 检查是否可以撤销/重做
	const canUndo = promptVersions.currentIndex > 0;
	const canRedo =
		promptVersions.currentIndex < promptVersions.versions.length - 1;

	// Magic Enhance 提示词增强功能
	const enhancePromptMutation = useMutation({
		mutationFn: async (currentPrompt: string) => {
			const response = await apiClient["prompt-editor"].enhance.$post({
				json: {
					prompt: currentPrompt,
					// 图生图写的是改图指令，走只澄清不加料的那套规则
					modelType:
						activeTab === "image-to-image" ? "image-edit" : "flux",
					imageCount: inputImages.length,
				},
			});

			if (!response.ok) {
				throw new Error("Failed to enhance prompt");
			}

			return response.json();
		},
		onSuccess: (data) => {
			const { enhancedPrompt } = data;
			updatePrompt(enhancedPrompt);
			console.log("Prompt enhanced");
		},
		onError: (error) => {
			toast.error("Error enhancing prompt");
			console.error(error);
		},
	});

	// Edit with AI 提示词编辑功能
	const editPromptMutation = useMutation({
		mutationFn: async ({
			currentPrompt,
			editInstruction,
		}: {
			currentPrompt: string;
			editInstruction: string;
		}) => {
			const response = await apiClient["prompt-editor"].edit.$post({
				json: {
					prompt: currentPrompt,
					editInstruction,
				},
			});

			if (!response.ok) {
				throw new Error("Failed to edit prompt");
			}

			return response.json();
		},
		onSuccess: (data) => {
			const { editedPrompt } = data;
			updatePrompt(editedPrompt);
			console.log("Prompt edited");
		},
		onError: (error) => {
			toast.error("Error editing prompt");
			console.error(error);
		},
	});

	// 增强提示词
	const handleEnhancePrompt = useCallback(() => {
		if (!prompt.trim()) {
			toast.error("Please enter a prompt first");
			return;
		}

		enhancePromptMutation.mutate(prompt);
	}, [prompt, enhancePromptMutation]);

	// 编辑提示词
	const handleEditPrompt = useCallback(
		(editInstruction: string) => {
			if (!prompt.trim()) {
				toast.error("Please enter a prompt first");
				return;
			}

			editPromptMutation.mutate({
				currentPrompt: prompt,
				editInstruction,
			});
		},
		[prompt, editPromptMutation],
	);

	// 生成中的占位卡片。后端要等出图成功才写库，所以"生成中"这一态只存在于前端。
	// 它不能放进 history 的 query 缓存里：那份缓存 30 秒就过期，切走标签页再切回来
	// 触发的 refetch 会拿服务端结果整包替换，占位卡就没了（按钮还在转圈）。
	// 同时只可能有一个生成在跑（handleGenerateImage / handleRegenerate 都拦着），
	// 所以落地时直接整个替换，收尾时整个清空。
	const [pendingItems, setPendingItems] = useState<PendingHistoryItem[]>([]);
	const addPendingItem = useCallback((item: PendingHistoryItem) => {
		setPendingItems([item]);
	}, []);
	// 等新记录真的回到列表里再撤占位卡，否则中间会闪一下空
	const settlePendingItems = useCallback(async () => {
		await queryClient
			.invalidateQueries({ queryKey: GENERATION_HISTORY_QUERY_KEY })
			.catch(() => {
				// 刷新失败也得把占位卡撤掉，不然它会一直挂在那
			});
		setPendingItems([]);
	}, [queryClient]);

	// kie 模型改成异步任务后，库里会有 PENDING / PROCESSING 的图片行。当前这一单由
	// generateImageMutation 自己等；刷新或关页再回来时没人在等的，由页面在历史刷新后
	// 调 resumeImageTasks 接着查到结束。这个集合防止同一个任务被查两路。
	const watchedImageTaskIdsRef = useRef(new Set<string>());
	const resumeImageTasks = useCallback(
		(
			items: Array<{
				id: string;
				type?: string;
				status?: string;
				model?: string;
			}>,
		) => {
			for (const item of items) {
				if (
					item.type !== "image" ||
					(item.status !== "PENDING" &&
						item.status !== "PROCESSING") ||
					watchedImageTaskIdsRef.current.has(item.id)
				) {
					continue;
				}
				watchedImageTaskIdsRef.current.add(item.id);
				waitForImageTask(item.id)
					// 续上的任务失败只上报不弹 toast：用户可能早就离开过这个页面
					.catch((error) =>
						reportGenerationFailure(
							"image-generator",
							item.model,
							error,
						),
					)
					.finally(() => {
						queryClient.invalidateQueries({
							queryKey: GENERATION_HISTORY_QUERY_KEY,
						});
						queryClient.invalidateQueries({
							queryKey: ["credits"],
						});
					});
			}
		},
		[queryClient],
	);

	// 图像生成mutation
	const generateImageMutation = useMutation({
		mutationFn: async (requestData: {
			prompt: string;
			model: ImageGeneratorApiModel;
			aspect_ratio?: string;
			num_outputs?: number;
			isPublic?: boolean;
			resolution?: string;
			inputType?: "text" | "image";
			input_image?: string;
			image_input?: string[];
		}) => {
			const inputImageCount = requestData.image_input?.length ?? 0;
			trackAiEditImageEvent(
				pathname,
				"ai-edit-image:generation-started",
				{
					model: requestData.model,
					inputType: requestData.inputType ?? "text",
					requestedOutputs: requestData.num_outputs ?? 1,
					// 10/21 @ Reference 上线后看：多图生成里有多少在 prompt 里按编号指图
					inputImages: inputImageCount,
					refs: countImageRefs(requestData.prompt, inputImageCount),
				},
			);

			// Use the model directly instead of mapping
			const response = await apiClient["image-generator"].$post({
				json: {
					...requestData,
					inputType: requestData.inputType || "text",
					sourcePage,
				},
			});

			if (!response.ok) {
				// Try to parse the error response. HTTPException 500 and Vercel
				// 504 come back as plain text, so keep the raw body for reporting.
				const rawBody = await response.text().catch(() => "");
				let errorData = {} as ErrorResponse;
				try {
					errorData = JSON.parse(rawBody) as ErrorResponse;
				} catch {}

				// Handle specific error types
				if (errorData.error === "NO_MEDIA_GENERATED") {
					const error = new Error(
						errorData.message || "Try another image or prompt.",
					) as MutationError;
					error.code = "NO_MEDIA_GENERATED";
					error.status = response.status;
					throw error;
				}

				if (errorData.error === "NSFW_CONTENT_DETECTED") {
					const error = new Error(
						errorData.message ||
							"NSFW content detected. Please modify your prompt and try again.",
					) as MutationError;
					error.code = "NSFW_CONTENT_DETECTED";
					error.status = response.status;
					throw error;
				}

				// 付费专属模型。下拉框已经锁了，但历史记录/My Assets 的重新生成
				// 会带着旧的 model 直接打过来，只有这条路能兜到。
				if (errorData.error === "PAID_MODEL_REQUIRED") {
					const error = new Error(
						errorData.message ||
							"This model is available on paid plans only.",
					) as MutationError;
					error.code = "PAID_MODEL_REQUIRED";
					error.status = response.status;
					throw error;
				}

				if (errorData.error === "CONTENT_MODERATION_BLOCKED") {
					const error = new Error(
						errorData.message ||
							"内容不符合生成要求，请修改提示词后重试。",
					) as MutationError;
					error.code = "CONTENT_MODERATION_BLOCKED";
					error.status = response.status;
					throw error;
				}

				// Handle insufficient credits error from backend
				if (errorData.error === "INSUFFICIENT_CREDITS") {
					throw new Error(
						errorData.message ||
							"Insufficient credits. Please purchase more credits to continue.",
					);
				}

				if (
					errorData.error === "TRIAL_EXHAUSTED" ||
					response.status === 401
				) {
					const error = new Error(
						errorData.message ||
							"Free trial used. Please sign in to continue using AI Image Generator.",
					) as MutationError;
					error.code = "TRIAL_EXHAUSTED";
					error.status = response.status;
					throw error;
				}

				if (!errorData.message) {
					// Untyped server failure: the body is only for reporting, never
					// for the keyword routing in onError (an upstream "credits"
					// message must not open the upgrade modal).
					const error = new Error(
						`Failed to generate image (${response.status}${rawBody ? `: ${rawBody.slice(0, 120)}` : ""})`,
					) as MutationError;
					error.code = "SERVER_ERROR";
					error.status = response.status;
					throw error;
				}

				throw new Error(errorData.message);
			}

			const responseData = (await response.json()) as
				| ImageGenerationResponse
				| ImageTaskSubmitted;
			if (!isImageTaskSubmitted(responseData)) {
				return responseData;
			}

			// kie 模型：任务已提交，库里已有 PENDING 行。先让历史拿到它、撤掉本地占位卡，
			// 再在这里等出图——onSuccess / onError 的处理和同步出图完全一样
			watchedImageTaskIdsRef.current.add(responseData.id);
			await settlePendingItems();
			const imageUrls = await waitForImageTask(responseData.id);
			return { imageUrls } as Pick<ImageGenerationResponse, "imageUrls"> &
				Partial<ImageGenerationResponse>;
		},
		onSuccess: async (data, requestData) => {
			if (data.imageUrls?.length) {
				trackAiEditImageEvent(
					pathname,
					"ai-edit-image:generation-succeeded",
					{
						model: requestData.model,
						inputType: requestData.inputType ?? "text",
						outputCount: data.imageUrls.length,
					},
				);
			}

			// 保存原始图片URL用于下载（使用第一张图片）
			const firstImageUrl = data.imageUrls?.[0];
			if (firstImageUrl) {
				setOriginalImageUrl(firstImageUrl);
			}

			// 检查是否需要在页面显示时添加水印
			const needsWatermark = shouldAddWatermarkOnClient(
				canRemoveWatermark,
				watermark,
			);

			// Debug logging
			console.log("[AI Image Generator] Watermark check:", {
				canRemoveWatermark,
				watermark,
				needsWatermark,
				activePlan: activePlan?.id,
			});

			if (needsWatermark && firstImageUrl) {
				try {
					// 获取图片并添加水印
					const response = await fetch(firstImageUrl);
					const imageBlob = await response.blob();

					const watermarkedBlob = await addCanvasWatermark(
						imageBlob,
						{
							text: "ImagesArt.ai",
							position: "top-right",
							fontSize: 26,
							color: "rgba(255, 255, 255, 0.4)",
							margin: 30,
							opacity: 0.6,
						},
					);

					// 创建带水印的图片URL用于显示
					const watermarkedUrl = URL.createObjectURL(watermarkedBlob);
					setGeneratedImage(watermarkedUrl);
					console.log(
						"[AI Image Generator] Watermark added successfully",
					);
				} catch (watermarkError) {
					console.error(
						"[AI Image Generator] Failed to add watermark:",
						watermarkError,
					);
					// 如果水印失败，显示原图
					setGeneratedImage(firstImageUrl);
				}
			} else if (firstImageUrl) {
				// 不需要水印，直接显示原图
				console.log(
					"[AI Image Generator] No watermark needed, showing original",
				);
				setGeneratedImage(firstImageUrl);
			}

			// Note: Credits are already consumed by the backend API
			// Refresh credits to reflect the updated balance
			queryClient.invalidateQueries({ queryKey: ["credits"] });

			// Refresh image generation history to show the newly generated image
			await settlePendingItems();

			if (firstImageUrl) {
				schedulePostGenerationPaywall();
			}

			// 处理翻译信息
			if (data.wasTranslated && data.translatedPrompt) {
				console.log(
					`Your prompt was translated from ${data.detectedLanguage} to English for better results.`,
				);
			}
		},
		onError: async (error, variables) => {
			setPendingItems([]);
			// 异步任务失败时库里那行已是 FAILED，刷新历史让卡片变成失败态
			queryClient.invalidateQueries({
				queryKey: GENERATION_HISTORY_QUERY_KEY,
			});
			reportGenerationFailure("image-generator", variables.model, error);

			// Display more specific error messages based on the error
			if (error instanceof Error) {
				const typedError = error as MutationError;

				if (typedError.code === "NO_MEDIA_GENERATED") {
					toast.dismiss();
					toast.error(t("imageSettings.errors.processImagesFailed"));
					return;
				}

				if (isImageGenerationTimeout(error.message)) {
					toast.error(t("imageSettings.errors.generationTimeout"));
					return;
				}

				if (typedError.code === "SERVER_ERROR") {
					toast.error(t("imageSettings.errors.errorGeneratingImage"));
					console.error(error);
					return;
				}

				// 付费专属模型：弹升级弹窗而不是 toast——用户需要的是升级入口，
				// 不是一句「生成失败」。
				if (typedError.code === "PAID_MODEL_REQUIRED") {
					setPendingUpgradeReason(
						paidModelPaywallReason(variables.model),
					);
					setShowUpgradeModal(true);
					return;
				}

				// If the error indicates NSFW or safety content, show a localized message
				if (
					typedError.code === "CONTENT_MODERATION_BLOCKED" ||
					typedError.code === "NSFW_CONTENT_DETECTED" ||
					error.message.includes("NSFW") ||
					error.message.includes("safety filter") ||
					error.message.includes("safety policy") ||
					error.message.includes("inappropriate content") ||
					error.message.includes("Sensitive content") ||
					error.message.includes("flagged") ||
					error.message.includes("content checker")
				) {
					// 模型拒图要说清楚是内容被判敏感、没扣分、可以换模型，否则用户以为是
					// 随机出错原样重试（10/25 芝加哥用户连试 5 次才来问）
					toast.error(
						typedError.code === "CONTENT_MODERATION_BLOCKED"
							? t("imageSettings.errors.contentModerationBlocked")
							: t("imageSettings.errors.contentFlagged"),
					);
				} else if (
					error.message.includes("Insufficient credits") ||
					error.message.includes("You need") ||
					error.message.includes("credit")
				) {
					// Handle insufficient credits error from backend
					setShowUpgradeModal(true);
				} else {
					toast.error(t("imageSettings.errors.errorGeneratingImage"));
				}
			} else {
				toast.error(t("imageSettings.errors.errorGeneratingImage"));
			}
			console.error(error);
		},
	});

	// Helper function to get style display name
	const getStyleDisplayName = (styleValue: ImageStyle): string => {
		const styleMap: Record<ImageStyle, string> = {
			none: "",
			photorealistic: "Photorealistic",
			"pixar-art": "Pixar Art",
			anime: "Anime",
			comic: "Comic",
			lego: "Lego",
			cinematic: "Cinematic",
			watercolor: "Watercolor",
		};
		return styleMap[styleValue] || "";
	};

	const [isSubmitting, setIsSubmitting] = useState(false);

	// Custom API mutation for templates with useCustomApi
	const customApiMutation = useMutation({
		mutationFn: async (requestData: {
			imageUrl?: string;
			imageBase64?: string;
			requestId?: string;
		}) => {
			if (!selectedTemplate?.behavior?.customApiEndpoint) {
				throw new Error("No custom API endpoint configured");
			}

			const response = await fetch(
				selectedTemplate.behavior.customApiEndpoint,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify({ ...requestData, sourcePage }),
				},
			);

			if (!response.ok) {
				const errorData = (await response
					.json()
					.catch(() => ({}))) as ErrorResponse;
				if (
					errorData.error === "INSUFFICIENT_CREDITS" ||
					response.status === 402
				) {
					throw new Error(
						errorData.message ||
							"Insufficient credits. Please purchase more credits to continue.",
					);
				}

				if (
					errorData.error === "TRIAL_EXHAUSTED" ||
					response.status === 401
				) {
					const error = new Error(
						errorData.message ||
							"Free trial used. Please sign in to continue using AI Image Generator.",
					) as MutationError;
					error.code = "TRIAL_EXHAUSTED";
					error.status = response.status;
					throw error;
				}
				throw new Error(errorData.message || "Failed to process image");
			}

			return response.json();
		},
		onSuccess: async (data) => {
			const imageUrl = data.data?.imageUrl;
			if (imageUrl) {
				setOriginalImageUrl(imageUrl);
				setGeneratedImage(imageUrl);
			}

			// Refresh credits
			queryClient.invalidateQueries({ queryKey: ["credits"] });
			await settlePendingItems();

			if (imageUrl) {
				schedulePostGenerationPaywall();
			}
		},
		onError: async (error) => {
			setPendingItems([]);
			// 走自定义接口的模板没有 model，用接口地址区分是哪个工具
			reportGenerationFailure(
				"image-generator",
				selectedTemplate?.behavior?.customApiEndpoint,
				error,
			);

			if (error instanceof Error) {
				const typedError = error as MutationError;

				if (
					error.message.includes("Insufficient credits") ||
					error.message.includes("credit")
				) {
					setShowUpgradeModal(true);
				} else {
					toast.error(error.message);
				}
			} else {
				toast.error("Error processing image");
			}
			console.error(error);
		},
	});

	// 存好表单再弹登录，登录回来原样恢复。访客缺图/缺字点 Create 也走这里
	const requestLogin = async () => {
		setIsSubmitting(true);
		await savePendingGeneratorState();
		showLoginModal(getCurrentPath());
		setIsSubmitting(false);
	};

	const handleGenerateImage = async () => {
		// Skip prompt check for templates with hidePrompt
		const skipPromptCheck =
			activeTab === "templates" && selectedTemplate?.behavior?.hidePrompt;
		if (
			(!skipPromptCheck && !prompt.trim()) ||
			generateImageMutation.isPending ||
			customApiMutation.isPending ||
			isSubmitting
		)
			return;

		// Validate image-to-image mode requires input images
		if (activeTab === "image-to-image" && inputImages.length === 0) {
			toast.error("请先上传至少一张图片");
			return;
		}

		// Validate templates with custom API require input images
		if (
			activeTab === "templates" &&
			selectedTemplate?.behavior?.useCustomApi &&
			inputImages.length === 0
		) {
			toast.error("请先上传一张图片");
			return;
		}

		// Check if user is authenticated
		if (!user) {
			// Guest trial is temporarily disabled — always require login
			await requestLogin();
			return;
		}

		setIsSubmitting(true);
		try {
			// Check if template uses custom API
			if (
				activeTab === "templates" &&
				selectedTemplate?.behavior?.useCustomApi
			) {
				// Skip credit check for guest users (trial already validated above)
				if (user) {
					const creditCost =
						selectedTemplate.behavior.creditCost || 1;
					const remainingCredits =
						await getRemainingCreditsWhenLoaded("IMAGE_GENERATION");
					// 上一次点击还在等积分
					if (remainingCredits === null) return;

					if (remainingCredits < creditCost) {
						setShowUpgradeModal(true);
						setIsSubmitting(false);
						return;
					}
				}

				// For custom API, send the first input image
				const firstImage = inputImages[0];
				const isBase64 = firstImage?.startsWith("data:");

				// Create optimistic pending item for custom API
				// For templates with hidePrompt, use empty string to hide prompt in history
				const displayPrompt = selectedTemplate.behavior?.hidePrompt
					? ""
					: selectedTemplate.prompt || "";
				addPendingItem({
					id: `pending-${Date.now()}`,
					originalPrompt: displayPrompt,
					model:
						selectedTemplate.behavior?.model?.default ||
						"background-remover",
					imageUrls: [],
					inputImageUrls: [firstImage],
					inputType: "image",
					createdAt: new Date().toISOString(),
					status: "PENDING",
					parameters: {
						num_outputs: 1,
					},
					metadata: {
						timestamp: new Date().toISOString(),
						parameters: {
							model:
								selectedTemplate.behavior?.model?.default ||
								"background-remover",
							num_outputs: 1,
						},
					},
				});

				// 占位卡由 mutation 的 onSuccess / onError 统一收尾
				await customApiMutation.mutateAsync({
					imageUrl: isBase64 ? undefined : firstImage,
					imageBase64: isBase64 ? firstImage : undefined,
					requestId: `bg-remove-${Date.now()}`,
				});

				setIsSubmitting(false);
				return;
			}

			// Determine input type: templates with images should be image-to-image
			const hasInputImages = inputImages.length > 0;
			const isImageMode =
				activeTab === "image-to-image" ||
				(activeTab === "templates" && hasInputImages);

			// background-remover 走 behavior.customApiEndpoint，上面那个分支已经 return；
			// 能走到这里说明模板配置和分发逻辑对不上，早点炸比发一个必然 400 的请求好
			if (selectedModel === "background-remover") {
				throw new Error(
					"background-remover must be dispatched to its custom API endpoint",
				);
			}

			// v1.0 only supports text-to-image, use v2.0 for image-to-image mode
			const modelToUse: ImageGeneratorApiModel =
				isImageMode && selectedModel === "v1.0"
					? "v2.0"
					: selectedModel;

			// Skip credit check for guest users (trial already validated above)
			if (user) {
				const creditCost = getImageGenerationCreditCost(
					modelToUse,
					resolution,
					imageCount,
					modelConfigs,
					isImageMode && hasInputImages,
				);

				// 检查是否有足够的图像生成信用额度
				const remainingCredits =
					await getRemainingCreditsWhenLoaded("IMAGE_GENERATION");
				// 上一次点击还在等积分
				if (remainingCredits === null) return;
				if (remainingCredits < creditCost) {
					// 显示升级模态框而不是直接跳转
					setShowUpgradeModal(true);
					return;
				}
			}

			// Apply style prefix if a style is selected
			let finalPrompt = prompt;
			if (style && style !== "none") {
				const styleDisplayName = getStyleDisplayName(style);
				finalPrompt = `${styleDisplayName} | ${prompt}`;
			}

			const requestData: {
				prompt: string;
				model: ImageGeneratorApiModel;
				aspect_ratio?: string;
				num_outputs?: number;
				isPublic?: boolean;
				resolution?: string;
				inputType?: "text" | "image";
				input_image?: string;
				image_input?: string[];
			} = {
				prompt: finalPrompt,
				model: modelToUse,
				aspect_ratio: aspectRatio,
				num_outputs: imageCount,
				isPublic: false,
				inputType: isImageMode ? "image" : "text",
			};

			// Add input images for image-to-image mode or templates with images
			if (isImageMode && hasInputImages) {
				requestData.image_input = inputImages;
			}

			// 添加清晰度参数（所有支持分辨率的模型）
			if (supportsImageResolution(modelToUse)) {
				requestData.resolution = resolution;
			}

			// Create optimistic pending item
			addPendingItem({
				id: `pending-${Date.now()}`,
				originalPrompt: finalPrompt,
				model: modelToUse,
				imageUrls: [],
				inputImageUrls:
					activeTab === "image-to-image" ? inputImages : undefined,
				inputType: activeTab === "image-to-image" ? "image" : "text",
				createdAt: new Date().toISOString(),
				status: "PENDING",
				parameters: {
					num_outputs: imageCount,
				},
				metadata: {
					timestamp: new Date().toISOString(),
					parameters: {
						model: modelToUse,
						aspect_ratio: aspectRatio,
						num_outputs: imageCount,
						resolution,
					},
				},
			});

			// mutateAsync returns a promise that resolves when the mutation is complete
			// but we want to reset isSubmitting slightly earlier so we'll just use mutate and reset in finally
			// 占位卡由 mutation 的 onSuccess / onError 统一收尾
			await generateImageMutation.mutateAsync(requestData);
		} catch (error) {
			// error is already handled by mutation onError, but we need to catch it here to release the lock
			console.error(
				"[useAiImageGenerator] handleGenerateImage error:",
				error,
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Convert image URL to base64
	const convertImageUrlToBase64 = useCallback(
		async (imageUrl: string): Promise<string> => {
			try {
				// Use image proxy for external URLs to avoid CORS issues
				let fetchUrl = imageUrl;
				if (imageUrl.startsWith("http")) {
					fetchUrl = `/api/image-proxy?url=${encodeURIComponent(imageUrl)}`;
				}
				const response = await fetch(fetchUrl);
				if (!response.ok) {
					throw new Error(
						`Failed to fetch image: ${response.statusText}`,
					);
				}
				const blob = await response.blob();
				return new Promise<string>((resolve, reject) => {
					const reader = new FileReader();
					reader.onload = () => {
						const base64String = reader.result as string;
						resolve(base64String);
					};
					reader.onerror = reject;
					reader.readAsDataURL(blob);
				});
			} catch (error) {
				console.error("Failed to convert image URL to base64:", error);
				throw error;
			}
		},
		[],
	);

	// Handle template selection
	const handleSelectTemplate = useCallback(
		async (template: Template) => {
			setSelectedTemplate(template);
			// Set prompt from template
			setPrompt(template.prompt, "templates");
			// Set model from template config, fallback to nano-banana for backward compatibility
			const defaultModel = template.behavior?.model?.default || "v3.0";
			setSelectedModel(defaultModel);
			// Set resolution from template config when provided
			setResolution(template.behavior?.resolution?.default || "1K");
			// 比例同样从模板配置取。缺了这一行时 behavior.defaultAspectRatio 在模板
			// 页上是个死字段——唯一读它的 effect 只在 size === "original"（历史遗留值）
			// 时才跑，正常进页面 aspectRatio 一直是 useState 的初始值 "1:1"。现有模板
			// 恰好全配 1:1 所以没人踩到，但去水印这类必须跟随原图的模板会被强行改比例。
			setAspectRatio(
				template.behavior?.defaultAspectRatio ??
					(supportsAutoAspectRatio(defaultModel) ? "auto" : "1:1"),
			);

			// First, clear inputImages immediately to ensure we don't show images from the previous template
			setInputImages([]);

			// Check if user previously uploaded images for THIS specific template (survives login/refresh)
			const restoredUploads = readTemplateUploads(template.id);
			if (restoredUploads && restoredUploads.length > 0) {
				setInputImages(restoredUploads);
				return;
			}

			// Load example images if available (no saved uploads for this template)
			if (template.exampleImages && template.exampleImages.length > 0) {
				try {
					const base64Images = await Promise.all(
						template.exampleImages.map((url) =>
							convertImageUrlToBase64(url),
						),
					);
					// 示例图要拉几百毫秒：这期间用户传了图、或从别的页带着作品回来了
					// （image-home.ts），就别拿示例图盖掉
					setInputImages((current) =>
						current.length > 0 ? current : base64Images,
					);
				} catch (error) {
					console.error("Failed to load example images:", error);
					// Continue even if example images fail to load
				}
			}
			// inputImages already cleared above, no need for else clause
		},
		[setPrompt, convertImageUrlToBase64],
	);

	// Persist user uploads on template pages so they survive login/refresh.
	useEffect(() => {
		const templateId = selectedTemplate?.id;
		if (activeTab !== "templates" || !templateId) return;

		if (inputImages.length === 0) {
			clearTemplateUploads(templateId);
			return;
		}

		// Only persist real URLs (template examples are base64 data URLs).
		const urls = inputImages.filter((image) => !image.startsWith("data:"));
		if (urls.length === 0) return;

		writeTemplateUploads(templateId, urls);
	}, [activeTab, selectedTemplate?.id, inputImages]);

	// Clear selected template
	const handleClearTemplate = useCallback(() => {
		setSelectedTemplate(null);
		setInputImages([]);
	}, []);

	// 只管显示的价（Create 上的数和模型下拉每行），发请求另有判据。图生图 tab 没图
	// 提交不了，发得出去的一定带图，所以没传图也按图生图算，不然传图前下拉少报加价；
	// 模板 tab 有的模板不要图，仍看有没有传
	const isImageToImage =
		activeTab === "image-to-image" ||
		(activeTab === "templates" && inputImages.length > 0);

	return {
		selectedModel,
		setSelectedModel,
		prompt,
		setPrompt,
		style,
		setStyle,
		generatedImage,
		originalImageUrl,
		usesRemaining: getRemainingCredits("IMAGE_GENERATION"),
		getRemainingCreditsWhenLoaded,
		isPending:
			generateImageMutation.isPending ||
			customApiMutation.isPending ||
			isSubmitting ||
			isUsingCredit,
		isCreditsLoading,
		handleGenerateImage,
		requestLogin,
		generateImageMutation,
		pendingItems,
		addPendingItem,
		resumeImageTasks,
		handleUndo,
		handleRedo,
		canUndo,
		canRedo,
		handleEnhancePrompt,
		isEnhancing: enhancePromptMutation.isPending,
		setAspectRatio,
		modelConfigs,
		handleEditPrompt,
		isEditing: editPromptMutation.isPending,
		watermark,
		setWatermark,
		showUpgradeModal,
		setShowUpgradeModal,
		pendingUpgradeReason,
		setPendingUpgradeReason,
		activePlan,
		aspectRatio,
		imageCount,
		setImageCount,
		resolution,
		setResolution,
		inputImages,
		setInputImages,
		selectedTemplate,
		setSelectedTemplate: handleSelectTemplate,
		clearTemplate: handleClearTemplate,
		currentCreditCost: getImageGenerationCreditCost(
			selectedModel,
			resolution,
			imageCount,
			modelConfigs,
			isImageToImage,
		),
		// 模型下拉每行的价。isPaid 由组件给（各自的付费判据带 purchasesLoading）
		creditCostAfterSwitch: (model: AIModel, isPaid: boolean) =>
			getCreditCostAfterSwitch(
				model,
				resolution,
				imageCount,
				modelConfigs,
				isImageToImage,
				isPaid,
			),
		convertImageUrlToBase64,
	};
}
